import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useAuth } from '@/context/AuthContext';
import { resolveMediaUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, ArrowUp, ArrowDown, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CategoriesList() {
  const { token } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/categories`);
      setCategories(data);
    } catch (e) {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const move = async (idx, dir) => {
    const target = idx + dir;
    if (target < 0 || target >= categories.length) return;
    const next = [...categories];
    [next[idx], next[target]] = [next[target], next[idx]];
    setCategories(next);
    const payload = {};
    next.forEach((c, i) => { payload[c.id] = i + 1; });
    try {
      await axios.post(`${API}/categories/reorder`, payload, { headers });
      toast.success('Order saved');
    } catch {
      toast.error('Reorder failed');
      load();
    }
  };

  const toggleActive = async (cat) => {
    try {
      await axios.put(`${API}/categories/${cat.id}`, { is_active: !cat.is_active }, { headers });
      load();
    } catch {
      toast.error('Update failed');
    }
  };

  const remove = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name_en}"? Properties under it will keep working but won't show on the site nav.`)) return;
    try {
      await axios.delete(`${API}/categories/${cat.id}`, { headers });
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="categories-list-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Categories</h1>
          <p className="text-gray-500 text-sm mt-1">Manage homepage category cards, thumbnails and price-visibility defaults.</p>
        </div>
        <Link to="/dashboard/categories/new">
          <Button className="bg-gradient-to-r from-brand-orange to-brand-rust text-white" data-testid="add-category-btn">
            <Plus className="h-4 w-4 mr-2" /> Add Category
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-brand-orange" /></div>
      ) : (
        <div className="grid gap-4">
          {categories.map((cat, idx) => (
            <Card key={cat.id} data-testid={`category-row-${cat.slug}`}>
              <CardContent className="p-4 flex flex-col md:flex-row gap-4 items-start md:items-center">
                <div className="flex gap-1 md:flex-col">
                  <Button size="icon" variant="ghost" onClick={() => move(idx, -1)} disabled={idx === 0}>
                    <ArrowUp className="h-4 w-4" />
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => move(idx, 1)} disabled={idx === categories.length - 1}>
                    <ArrowDown className="h-4 w-4" />
                  </Button>
                </div>
                {cat.thumbnail_url ? (
                  <img src={resolveMediaUrl(cat.thumbnail_url)} alt="" className="w-20 h-20 object-cover rounded-lg" />
                ) : (
                  <div className="w-20 h-20 rounded-lg bg-gray-100" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-heading font-semibold text-gray-900">{cat.name_en}</h3>
                    <span className="text-xs text-gray-500 font-mono">/{cat.slug}</span>
                  </div>
                  {cat.name_hi && <p className="text-sm text-gray-600 mb-1">{cat.name_hi}</p>}
                  <p className="text-sm text-gray-500 line-clamp-1">{cat.short_description_en || ''}</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Active</span>
                    <Switch checked={cat.is_active} onCheckedChange={() => toggleActive(cat)} />
                  </div>
                  <Link to={`/dashboard/categories/${cat.id}/edit`}>
                    <Button size="sm" variant="outline"><Edit className="h-4 w-4" /></Button>
                  </Link>
                  <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(cat)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {!categories.length && (
            <Card><CardContent className="p-10 text-center text-gray-500">No categories yet. Create one to get started.</CardContent></Card>
          )}
        </div>
      )}
    </div>
  );
}
